import {
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  Text,
  CircularProgress,
  ModalV2,
  Icon,
  FAIconName,
  ChakraButton,
} from '@equip.health/ui';
import { useEffect, useState } from 'react';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import useS3FileDownload from '~/lib/hooks/useS3FileDownload';
import fileUtil from '~/lib/util/file.util';

const buttonProps: {
  height: string;
  padding: string;
  width: string;
} = {
  height: '40px',
  padding: '10px',
  width: '40px',
};

const TOP_LEVEL_ID = 'view-document-preview-modal';

const DocumentPreviewModal = ({
  isOpen,
  onClose,
  fileName,
  url,
}: {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  url: string;
}) => {
  const { trackEvent } = useAnalytics();

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  const {
    data: attachmentDownloadData,
    error: attachmentDownloadError,
    isFileDownloaded,
    loading: isAttachmentDownloading,
    sendRequest: sendAttachmentDownloadRequest,
  } = useS3FileDownload();

  useEffect(() => {
    if (attachmentDownloadError) {
      window.open(url, '_blank', 'noopener');
    }
  }, [attachmentDownloadError, url]);

  useEffect(() => {
    if (isFileDownloaded && attachmentDownloadData) {
      const blobURL = window.URL.createObjectURL(
        new Blob([attachmentDownloadData]),
      );
      const link = document.createElement('a');
      link.href = blobURL;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobURL);
    }
  }, [isFileDownloaded, attachmentDownloadData, fileName]);

  const viewHeight = isFullScreen ? 'calc(100vh - 140px)' : '65vh';

  return (
    <ModalV2
      hideCloseButton
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size={isFullScreen ? 'full' : '5xl'}
      title={
        <ChakraFlex justifyContent="space-between" width="100%">
          <Text variant="h5">{fileName ?? ''}</Text>
          <ChakraFlex>
            {fileUtil.isImageFile(fileName) && (
              <>
                {isAttachmentDownloading ? (
                  <ChakraBox {...buttonProps} marginRight="8px">
                    <CircularProgress
                      display="block"
                      id="attachment-download"
                      isIndeterminate
                      size="20px"
                    />
                  </ChakraBox>
                ) : (
                  <ChakraButton
                    {...buttonProps}
                    marginRight="8px"
                    onClick={() => {
                      trackEvent(
                        DocumentCenterEvents.documentPreviewModalDownloadClicked,
                        {
                          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                        },
                      );
                      sendAttachmentDownloadRequest({ url });
                    }}
                    variant="plain"
                  >
                    <Icon
                      color="neutral.secondary"
                      name={FAIconName.arrowDownToLine}
                    />
                  </ChakraButton>
                )}
              </>
            )}
            <ChakraButton
              {...buttonProps}
              marginRight="8px"
              onClick={() => {
                trackEvent(
                  DocumentCenterEvents.documentPreviewModalExpandClicked,
                  {
                    Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                  },
                );
                setIsFullScreen(!isFullScreen);
              }}
              variant="plain"
            >
              {isFullScreen ? (
                <Icon
                  color="neutral.secondary"
                  name={FAIconName.shrinkRegular}
                />
              ) : (
                <Icon
                  color="neutral.secondary"
                  name={FAIconName.maximizeRegular}
                />
              )}
            </ChakraButton>
            <ChakraButton
              {...buttonProps}
              onClick={() => {
                trackEvent(
                  DocumentCenterEvents.documentPreviewModalCloseClicked,
                  {
                    Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                  },
                );
                onClose();
              }}
              variant="plain"
            >
              <Icon color="neutral.secondary" name={FAIconName.timesRegular} />
            </ChakraButton>
          </ChakraFlex>
        </ChakraFlex>
      }
    >
      <ChakraBox>
        {fileUtil.isImageFile(fileName) ? (
          <ChakraBox position="relative">
            <ChakraImage
              alt={fileName}
              objectFit="contain"
              onLoad={() => setIsImageLoaded(true)}
              src={url}
              style={{ height: viewHeight }}
              visibility={isImageLoaded ? 'visible' : 'hidden'}
              width="100%"
            />
            {!isImageLoaded && (
              <ChakraFlex
                alignItems="center"
                bottom={0}
                justifyContent="center"
                left={0}
                position="absolute"
                right={0}
                top={0}
              >
                <CircularProgress
                  id="image-loading"
                  isIndeterminate
                  size="30px"
                />
              </ChakraFlex>
            )}
          </ChakraBox>
        ) : (
          <iframe
            src={url}
            style={{ height: viewHeight }}
            title={fileName}
            width="100%"
          />
        )}
      </ChakraBox>
    </ModalV2>
  );
};

export default DocumentPreviewModal;
